import React from 'react';

// Legal content organized by document ID
export const legalContent: Record<string, React.ReactNode> = {
  privacy: (
    <>
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">1. Who We Are</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          Talentees provide an AI-powered CV analysis platform that helps recruiters efficiently evaluate candidates by generating match scores based on professional experience and job requirements.
        </p>
        <div className="text-white/70 leading-relaxed space-y-2">
          <p><strong className="text-white/90">Company Information:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Legal Name: Talentees</li>
            <li>Registration Number: 992 604 942 00019</li>
            <li>Registered Address: 30 Boulevard de Sébastopol 75004</li>
            <li>Email: <a href="mailto:hello@talentees.net" className="text-[#28e675] hover:underline">hello@talentees.net</a></li>
            <li>Privacy Contact: <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a></li>
          </ul>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">2. Introduction</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We are committed to protecting your personal data and respecting your privacy. This Privacy Policy explains:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>What personal data we collect</li>
          <li>Why we collect it</li>
          <li>How we use it</li>
          <li>Who we share it with</li>
          <li>Your rights regarding your data</li>
          <li>How to contact us</li>
        </ul>
        <p className="text-white/70 leading-relaxed mt-4">
          This policy applies to:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mt-2">
          <li>Job candidates who upload CVs to our platform</li>
          <li>Recruiters and clients who use our services</li>
          <li>Visitors to our website</li>
        </ul>
        <p className="text-white/70 leading-relaxed mt-4">
          By using our service, you acknowledge that you have read and understood this Privacy Policy.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">3. What Personal Data We Collect</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">3.1 Data from Job Candidates</h3>
        <p className="text-white/70 leading-relaxed mb-4">When you upload your CV or create an account, we collect:</p>

        <p className="text-white/80 font-medium mb-2">Identity Information:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number (if provided in CV)</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Professional Information:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Work experience (job titles, companies, dates, descriptions)</li>
          <li>Education background (degrees, schools, dates)</li>
          <li>Skills and qualifications</li>
          <li>Certifications and achievements</li>
          <li>Any other information you include in your CV</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Technical Information:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Device type</li>
          <li>Upload date and time</li>
          <li>Login activity</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">We DO NOT intentionally collect:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Photos or images (even if in your CV, we remove them before AI processing)</li>
          <li>Date of birth or age</li>
          <li>Marital or family status</li>
          <li>Religion or beliefs</li>
          <li>Health information</li>
          <li>Racial or ethnic origin</li>
          <li>Political opinions</li>
          <li>Trade union membership</li>
          <li>Sexual orientation</li>
          <li>Any other special category data under GDPR Article 9</li>
        </ul>
        <p className="text-white/70 leading-relaxed italic">
          If such information appears in your CV, we remove it before AI analysis.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">3.2 Data from Recruiters/Clients</h3>

        <p className="text-white/80 font-medium mb-2">Account Information:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Company name</li>
          <li>Contact person name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Billing information (processed by secure payment provider)</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Usage Information:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
          <li>Job postings and requirements</li>
          <li>Search preferences</li>
          <li>Candidates reviewed</li>
          <li>Platform usage statistics</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">3.3 Data from Website Visitors</h3>

        <p className="text-white/80 font-medium mb-2">Technical Data:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>IP address (anonymized after 7 days)</li>
          <li>Browser type</li>
          <li>Pages visited</li>
          <li>Referring website</li>
          <li>Time and date of visit</li>
        </ul>

        <p className="text-white/70 leading-relaxed">
          <strong>Cookies:</strong> See our Cookie Policy for details
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">4. How We Collect Your Data</h2>
        <p className="text-white/70 leading-relaxed mb-4">We collect personal data when you:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Upload your CV to our platform</li>
          <li>Create an account</li>
          <li>Contact us by email or phone</li>
          <li>Use our website</li>
          <li>Subscribe to communications (if applicable)</li>
          <li>Provide information to recruiters through our platform</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-2">We do NOT:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Buy data from third parties</li>
          <li>Scrape data from public sources</li>
          <li>Use social media data</li>
          <li>Collect data without your knowledge</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">5. Why We Process Your Data</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          Under GDPR, we must have a legal basis for processing your personal data. We rely on:
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.1 Legitimate Interest</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          We process candidate CVs based on our legitimate interest in:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Providing an efficient CV analysis service to recruiters</li>
          <li>Helping candidates be evaluated fairly and objectively</li>
          <li>Operating our business</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Why this is balanced:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>You expect your CV to be analyzed when you upload it for job applications</li>
          <li>We use only job-relevant information</li>
          <li>You have full control (can object, delete, or request review)</li>
          <li>Our processing helps you get fair consideration for jobs</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.2 Contract Performance</h3>
        <p className="text-white/70 leading-relaxed mb-2">For our recruiter clients:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Providing the service they subscribed to</li>
          <li>Managing their accounts</li>
          <li>Processing payments</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.3 Consent</h3>
        <p className="text-white/70 leading-relaxed mb-2">For optional activities:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-2">
          <li>Sending marketing communications (if you opt-in)</li>
          <li>Using non-essential cookies (if applicable)</li>
        </ul>
        <p className="text-white/70 leading-relaxed">You can withdraw consent at any time.</p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.4 Legal Obligation</h3>
        <p className="text-white/70 leading-relaxed mb-2">For limited purposes:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Tax and accounting records (legal requirement in France)</li>
          <li>Responding to legal requests from authorities</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">6. How We Use Your Personal Data</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.1 CV Analysis Service</h3>

        <p className="text-white/80 font-medium mb-2">For Candidates:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Analyze your CV using AI technology (Mistral AI and Google Gemini API)</li>
          <li>Generate a match score based on job requirements</li>
          <li>Provide the score and analysis to recruiters</li>
          <li>Enable recruiters to review your application</li>
          <li>Respond to your questions and requests</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">For Recruiters:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Provide comparative reports of multiple candidates</li>
          <li>Show match scores and highlights</li>
          <li>Enable efficient candidate screening</li>
          <li>Provide platform access and support</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.2 Service Improvement</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Understand how our platform is used</li>
          <li>Identify technical issues</li>
          <li>Improve our AI analysis accuracy</li>
          <li>Develop new features</li>
        </ul>
        <p className="text-white/70 leading-relaxed mb-4 italic">
          <strong>Important:</strong> We do NOT use your CV data to train our AI models without separate explicit consent.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.3 Communication</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Send you account-related emails (application status, account updates)</li>
          <li>Respond to your support requests</li>
          <li>Send service announcements</li>
          <li>Optional: Send marketing communications if you opted in</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.4 Legal Compliance</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Comply with legal obligations</li>
          <li>Protect our legal rights</li>
          <li>Prevent fraud or abuse</li>
          <li>Respond to lawful requests from authorities</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">7. Automated Decision-Making and AI</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">7.1 How Our AI Works</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          We use artificial intelligence (Mistral AI, a French AI company, and Google Gemini API) to analyze CVs and generate match scores.
        </p>

        <p className="text-white/80 font-medium mb-2">What the AI does:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Reads CV content (experience, skills, education)</li>
          <li>Compares it to job requirements</li>
          <li>Generates a score from 1-10 based on match quality</li>
          <li>Highlights relevant experience and skills</li>
          <li>Identifies potential concerns or gaps</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">What the AI analyzes:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Years of relevant experience</li>
          <li>Specific skills mentioned</li>
          <li>Job title progression</li>
          <li>Education level and field</li>
          <li>Professional achievements</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">What the AI does NOT analyze:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
          <li>Your name, gender, or age</li>
          <li>Your photo or appearance</li>
          <li>Your address or location (unless specifically relevant to job requirements)</li>
          <li>Any protected characteristics</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">7.2 Important: AI Assists, Humans Decide</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Our AI provides recommendations ONLY. It does not make hiring decisions.</strong>
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Scores are advisory for recruiters</li>
          <li>Human recruiters review all candidates</li>
          <li>Recruiters can override AI scores</li>
          <li>Final hiring decisions are always made by humans</li>
          <li>Recruiters consider many factors beyond the AI score</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">7.3 Your Rights Regarding AI</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Understand how your score was calculated</li>
          <li>Request a human review of your CV</li>
          <li>Contest your score</li>
          <li>Provide additional context or explanation</li>
          <li>Object to automated processing</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>To request human review:</strong> Email{' '}
          <a href="mailto:hello@talentees.net" className="text-[#28e675] hover:underline">hello@talentees.net</a>{' '}
          with your application reference.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">8. Who We Share Your Data With</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We only share your personal data when necessary and with appropriate safeguards.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.1 Third-Party Service Providers</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          We share data with the following processors who help us operate our service:
        </p>

        <div className="space-y-6 mb-6">
          <div>
            <p className="text-white/80 font-medium mb-2">Mistral AI (France)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li><strong>What they do:</strong> AI-powered CV analysis</li>
              <li><strong>What we share:</strong> Pseudonymized CV content (we remove names, emails, and other direct identifiers before sending)</li>
              <li><strong>Location:</strong> France/European Union</li>
              <li><strong>Safeguards:</strong> Data Processing Agreement (DPA) in place, GDPR compliant</li>
              <li><strong>Retention:</strong> To check Mistral retention</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Google Cloud Platform - Gemini API (United States)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li><strong>What they do:</strong> AI-powered CV analysis</li>
              <li><strong>What we share:</strong> Pseudonymized CV content (we remove names, emails, and other direct identifiers before sending)</li>
              <li><strong>Location:</strong> United States</li>
              <li><strong>Safeguards:</strong> Data Processing Agreement (DPA), Standard Contractual Clauses (SCCs), EU-US Data Privacy Framework, API data not used to train models</li>
              <li><strong>Retention:</strong> API data processed transiently; retention per Google Cloud DPA</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Firebase (Google - Europe)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li><strong>What they do:</strong> Application data storage</li>
              <li><strong>What we share:</strong> Application data (encrypted)</li>
              <li><strong>Location:</strong> Europe (Belgium)</li>
              <li><strong>Safeguards:</strong> DPA in place, ISO 27001 certified, encryption at rest and in transit</li>
              <li><strong>Retention:</strong> Per our retention policy in Section 10</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Azure</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li><strong>What they do:</strong> VM processing and application connectivity</li>
              <li><strong>What we share:</strong> All data encrypted</li>
              <li><strong>Location:</strong> Varies by service region</li>
              <li><strong>Safeguards:</strong> DPA in place, GDPR compliant, ISO 27001 certified</li>
              <li><strong>Retention:</strong> Per our retention policy</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Stripe</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li><strong>What they do:</strong> Process subscription payments from recruiters</li>
              <li><strong>What we share:</strong> Payment information (we do not store credit card details)</li>
              <li><strong>Location:</strong> Dublin, Ireland</li>
              <li><strong>Safeguards:</strong> PCI-DSS compliant, DPA in place, Standard Contractual Clauses (SCCs)</li>
            </ul>
          </div>
        </div>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.2 Recruiters/Employers</h3>
        <p className="text-white/70 leading-relaxed mb-2">When you apply for a job through our platform:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Your CV and match score are shared with the recruiter who posted the job</li>
          <li>Only recruiters you apply to can see your information</li>
          <li>We share: Name, contact information, CV content, match score</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.3 Legal Requirements</h3>
        <p className="text-white/70 leading-relaxed mb-2">We may disclose your data if required by law:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>To comply with legal obligations</li>
          <li>To respond to valid legal requests from authorities</li>
          <li>To protect our rights, safety, or property</li>
          <li>To prevent fraud or abuse</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-2">We do NOT:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Sell your data to third parties</li>
          <li>Share your data for marketing purposes</li>
          <li>Provide your data to data brokers</li>
          <li>Share your data outside the purposes listed above</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">9. International Data Transfers</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          Your personal data is processed and stored both within and outside the European Union.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.1 Data Processing Locations</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          We transfer personal data to the following service providers:
        </p>

        <div className="space-y-6 mb-6">
          <div>
            <p className="text-white/80 font-medium mb-2">Mistral AI (France)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li>Purpose: AI-powered CV analysis</li>
              <li>Location: France/European Union</li>
              <li>Safeguards: EU-based, GDPR compliant</li>
              <li>Data retention: Processed transiently for analysis; results stored per Section 10</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Google Cloud Platform - Gemini API (United States)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li>Purpose: AI-powered CV analysis</li>
              <li>Location: United States</li>
              <li>Safeguards: Standard Contractual Clauses (SCCs), EU-US Data Privacy Framework</li>
              <li>Data protection: API data is not used to train models (as per Google Cloud terms)</li>
              <li>Google is certified under the EU-US Data Privacy Framework</li>
              <li>Data retention: API requests processed transiently; no long-term storage by Google</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Firebase (Google - Europe)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li>Purpose: Application data storage</li>
              <li>Location: Europe (Belgium)</li>
              <li>Safeguards: Data Processing Agreement (DPA), ISO 27001 certified, encryption at rest and in transit</li>
              <li>Data retention: Per our retention policy in Section 10</li>
              <li>Note: As Firebase is hosted in Europe, no international transfer occurs for this service</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Microsoft Azure</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li>Purpose: Virtual machine processing and application connectivity</li>
              <li>Location: Varies by service region</li>
              <li>Safeguards: Standard Contractual Clauses (SCCs), EU-US Data Privacy Framework (when applicable), ISO 27001 certified</li>
              <li>Data retention: Per our retention policy in Section 10</li>
            </ul>
          </div>

          <div>
            <p className="text-white/80 font-medium mb-2">Stripe (United States)</p>
            <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
              <li>Purpose: Payment processing for recruiter subscriptions</li>
              <li>Location: United States (with EU operations in Ireland)</li>
              <li>Safeguards: PCI-DSS compliant, Standard Contractual Clauses (SCCs)</li>
              <li>Data retention: Per Stripe's data retention policy and legal requirements</li>
            </ul>
          </div>
        </div>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.2 Legal Safeguards for International Transfers</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          All transfers of personal data to countries outside the European Union are protected by appropriate safeguards as required by GDPR Article 46:
        </p>

        <p className="text-white/80 font-medium mb-2">Standard Contractual Clauses (SCCs):</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>We have implemented Standard Contractual Clauses approved by the European Commission with all non-EU service providers</li>
          <li>These clauses legally bind our service providers to protect your data according to EU standards</li>
          <li>Copies of our SCCs are available upon request by emailing{' '}
            <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
          </li>
        </ul>

        <p className="text-white/80 font-medium mb-2">EU-US Data Privacy Framework:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Google (Gemini API) and Microsoft (Azure) are certified under the EU-US Data Privacy Framework</li>
          <li>This framework provides additional safeguards for data transfers to the United States</li>
          <li>You can verify certifications at: <a href="https://www.dataprivacyframework.gov/" target="_blank" rel="noopener noreferrer" className="text-[#28e675] hover:underline">https://www.dataprivacyframework.gov/</a></li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Additional Technical Safeguards:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>All data transmitted internationally is encrypted in transit using TLS 1.3</li>
          <li>All data stored by our service providers is encrypted at rest using AES-256 or equivalent</li>
          <li>We conduct regular security assessments of our international data transfers</li>
          <li>We implement data minimization - only necessary data is transferred</li>
          <li>We pseudonymize CV data before sending to AI providers (removing names, emails, and direct identifiers)</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.3 Why We Use International Service Providers</h3>
        <p className="text-white/70 leading-relaxed mb-2">We use service providers located outside the EU because:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>They provide best-in-class AI technology (Gemini API and Mistral AI for advanced language understanding)</li>
          <li>They offer robust, scalable infrastructure (Azure for reliable service delivery)</li>
          <li>They are leaders in data security and privacy compliance</li>
          <li>These services enable us to provide you with the best possible CV analysis experience</li>
          <li>EU-only alternatives do not currently offer comparable AI capabilities</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.4 Your Rights Regarding International Transfers</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Be informed about where your data is processed (this section provides that information)</li>
          <li>Request detailed information about the safeguards in place for international transfers</li>
          <li>Object to international data transfers on grounds relating to your particular situation</li>
          <li>Request a copy of the Standard Contractual Clauses we have in place</li>
          <li>Withdraw your consent where processing is based on consent (though this may mean we cannot provide our service)</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-2">
          <strong>To exercise these rights:</strong> Contact us at{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
        </p>
        <p className="text-white/70 leading-relaxed">
          <strong>Important:</strong> If you object to international transfers, we may not be able to provide our Service to you, as our AI technology depends on these international service providers.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.5 Changes to Our Data Processing Locations</h3>
        <p className="text-white/70 leading-relaxed mb-2">If we add new service providers or change where data is processed, we will:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Update this Privacy Policy with the new information</li>
          <li>Notify you by email if the change significantly affects how your data is protected</li>
          <li>Ensure appropriate safeguards are in place before transferring data to any new location</li>
          <li>Give you the opportunity to object where applicable</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">10. How Long We Keep Your Data</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We retain personal data only as long as necessary for the purposes we collected it.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.1 Candidate Data</h3>

        <p className="text-white/80 font-medium mb-2">Active Applications:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Duration: While your application is active</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Unsuccessful Candidates:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Duration: <strong>Maximum 2 years</strong> from your last activity (last login or application)</li>
          <li>Reason: This allows recruiters to consider you for future positions</li>
          <li>After 2 years: Automatic permanent deletion</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Successful Candidates:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
          <li>Data is typically transferred to the employer and deleted from our system</li>
          <li>Retention: As needed for contract fulfillment + legal requirements</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.2 Recruiter/Client Data</h3>

        <p className="text-white/80 font-medium mb-2">Active Accounts:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Duration: While account is active</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Closed Accounts:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
          <li>Duration: 1 year after account closure (for support/dispute resolution)</li>
          <li>Exception: Financial records retained for 10 years (legal requirement in France)</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.3 Technical and Log Data</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li><strong>Access logs:</strong> 12 months</li>
          <li><strong>Analytics data:</strong> 13 months</li>
          <li><strong>IP addresses:</strong> Anonymized after 7 days</li>
          <li><strong>Error logs:</strong> 12 months</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.4 Backups</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Daily backups: 30 days</li>
          <li>Monthly backups: 12 months</li>
          <li>When data is deleted from production, it's removed from backups within 90 days</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.5 Early Deletion</h3>
        <p className="text-white/70 leading-relaxed">
          You can request deletion of your data at any time (see Section 12 - Your Rights).
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">11. How We Protect Your Data</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We implement appropriate technical and organizational measures to protect your personal data:
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">11.1 Technical Security</h3>

        <p className="text-white/80 font-medium mb-2">Encryption:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>All data transmitted over the internet is encrypted using TLS 1.3</li>
          <li>All data stored in our databases is encrypted at rest using AES-256 encryption</li>
          <li>Backups are encrypted</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Access Controls:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Role-based access control - staff can only access data needed for their job</li>
          <li>Multi-factor authentication for admin accounts</li>
          <li>Regular access reviews</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Security Monitoring:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>24/7 security monitoring</li>
          <li>Intrusion detection systems</li>
          <li>Regular security audits</li>
          <li>Annual penetration testing</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Infrastructure:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Secure cloud hosting in EU data centers</li>
          <li>Regular security updates and patches</li>
          <li>Firewalls and network security</li>
          <li>Automated backup systems</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">11.2 Organizational Security</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Staff training on data protection</li>
          <li>Confidentiality agreements for all employees</li>
          <li>Background checks for staff with data access</li>
          <li>Incident response plan</li>
          <li>Regular security awareness training</li>
          <li>Data Protection Impact Assessments</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">11.3 Data Minimization</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>We collect only data necessary for our service</li>
          <li>We delete data when no longer needed</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">11.4 Breach Notification</h3>
        <p className="text-white/70 leading-relaxed mb-2">In the unlikely event of a data breach:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>We will notify CNIL (French data protection authority) within 72 hours if required</li>
          <li>We will notify affected individuals without undue delay if there is a high risk</li>
          <li>We have an incident response plan ready to act quickly</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">12. Your Rights</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          Under GDPR, you have the following rights regarding your personal data:
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.1 Right of Access</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Know what personal data we hold about you</li>
          <li>Receive a copy of your data</li>
          <li>Understand how we use it</li>
        </ul>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>How to exercise:</strong> Email{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>{' '}
          with "Access Request" in the subject line.
        </p>
        <p className="text-white/70 leading-relaxed">
          <strong>Response time:</strong> Within 30 days (free of charge)
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.2 Right to Rectification</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Correct inaccurate personal data</li>
          <li>Complete incomplete data</li>
        </ul>
        <p className="text-white/70 leading-relaxed mb-2"><strong>How to exercise:</strong></p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-2">
          <li>Update your profile in your account settings</li>
          <li>Or email{' '}
            <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
          </li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>Response time:</strong> Within 30 days
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.3 Right to Erasure / "Right to be Forgotten"</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to request deletion of your personal data when:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>It's no longer necessary for the purpose it was collected</li>
          <li>You withdraw consent (if processing was based on consent)</li>
          <li>You object to processing and there are no overriding legitimate grounds</li>
          <li>The data was unlawfully processed</li>
        </ul>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Exceptions:</strong> We may retain data if required by law (e.g., accounting records for 10 years).
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>How to exercise:</strong> Email{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>{' '}
          with "Deletion Request" in the subject line.
        </p>
        <p className="text-white/70 leading-relaxed mb-2"><strong>What happens:</strong></p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-2">
          <li>We delete your data from all systems (database, files, backups)</li>
          <li>We request deletion from our processors (Mistral AI, Google, etc.)</li>
          <li>You receive confirmation within 30 days</li>
          <li>Your account will be permanently closed</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>Response time:</strong> Within 30 days
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.4 Right to Data Portability</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Receive your personal data in a structured, commonly used format (JSON or CSV)</li>
          <li>Transfer your data to another service</li>
        </ul>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>What's included:</strong> Data you provided to us (CV content, profile information)
        </p>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>What's NOT included:</strong> Derived data (AI scores, analysis results)
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>How to exercise:</strong> Email{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>{' '}
          with "Data Export Request"
        </p>
        <p className="text-white/70 leading-relaxed">
          <strong>Response time:</strong> Within 30 days
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.5 Right to Object</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          You have the right to object to processing based on legitimate interests.
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>For marketing:</strong> You can opt-out anytime by clicking "unsubscribe" in emails or contacting us.
        </p>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>For CV analysis:</strong> You can object, but this may mean we cannot provide our service to you.
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>How to exercise:</strong> Email{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>{' '}
          with "Objection"
        </p>
        <p className="text-white/70 leading-relaxed">
          <strong>Response time:</strong> We stop processing immediately (unless we have compelling legitimate grounds)
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.6 Right to Restrict Processing</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          You have the right to restrict how we process your data in certain circumstances:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>You contest the accuracy of your data (we restrict while verifying)</li>
          <li>Processing is unlawful but you don't want deletion</li>
          <li>We no longer need the data but you need it for legal claims</li>
          <li>You've objected and we're assessing whether our legitimate grounds override yours</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>How to exercise:</strong> Email{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.7 Rights Regarding Automated Decision-Making</h3>
        <p className="text-white/70 leading-relaxed mb-2">You have the right to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Not be subject to decisions based solely on automated processing</li>
          <li>Request human review of AI-generated scores</li>
          <li>Contest automated decisions</li>
          <li>Express your point of view</li>
        </ul>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Our commitment:</strong> All hiring decisions involve human review. Our AI provides recommendations only.
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          <strong>How to exercise:</strong> Email{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>{' '}
          with "Human Review Request"
        </p>
        <p className="text-white/70 leading-relaxed">
          <strong>Response time:</strong> Within 30 days
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.8 Right to Withdraw Consent</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          If processing is based on your consent, you can withdraw it at any time.
        </p>
        <p className="text-white/70 leading-relaxed mb-2"><strong>How to exercise:</strong></p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Marketing emails: Click "unsubscribe"</li>
          <li>Other: Email{' '}
            <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
          </li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>Effect:</strong> We stop processing based on consent, but other legal bases may still apply.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.9 How to Exercise Your Rights</h3>
        <p className="text-white/70 leading-relaxed mb-2"><strong>Contact us:</strong></p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Email:{' '}
            <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
          </li>
          <li>Subject line: Include the specific right (e.g., "Access Request", "Deletion Request")</li>
          <li>Include: Your name, email, and any account reference</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-2"><strong>What we'll do:</strong></p>
        <ol className="list-decimal list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Acknowledge your request within 24 hours</li>
          <li>Verify your identity (to protect your privacy)</li>
          <li>Process your request within 30 days</li>
          <li>Respond with the outcome</li>
        </ol>

        <p className="text-white/70 leading-relaxed mb-2">
          <strong>No fee:</strong> Exercising your rights is free (unless requests are manifestly unfounded or excessive)
        </p>
        <p className="text-white/70 leading-relaxed">
          <strong>Need help?</strong> Contact us at{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>{' '}
          - we're here to help!
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">13. Cookies</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We use cookies to improve your experience on our website.
        </p>

        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li><strong>Essential Cookies:</strong> Required for the website to function (login, security)</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site (with your consent)</li>
          <li><strong>Marketing Cookies:</strong> Track your activity for advertising purposes (with your consent)</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-4">
          For detailed information, see our Cookie Policy.
        </p>

        <p className="text-white/80 font-medium mb-2">Managing Cookies:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Click "Cookie Settings" in the website footer</li>
          <li>Configure your browser to block cookies</li>
          <li>Note: Blocking essential cookies may affect functionality</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">14. Children's Privacy</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.1 Age Restriction</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Our Service is not intended for, and may not be used by, individuals under the age of 18.</strong>
        </p>
        <p className="text-white/70 leading-relaxed">
          We do not knowingly collect personal data from children under 18. By using our Service, you represent and warrant that you are at least 18 years of age.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.2 Why We Have This Restriction</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          Our AI-powered CV analysis Service is designed exclusively for:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Job candidates of legal working age (18+)</li>
          <li>Professional recruiters and employers</li>
          <li>Business users engaged in employment activities</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          The recruitment process and employment decisions are activities intended for adults only. French labor law and GDPR require special protections for minors.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.3 What We Do If We Learn a Child Has Used Our Service</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          If we become aware that we have collected personal data from anyone under the age of 18, we will take immediate action:
        </p>

        <p className="text-white/80 font-medium mb-2">Within 24 hours:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Suspend the account immediately</li>
          <li>Begin the deletion process</li>
          <li>Notify our data protection officer</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Within 72 hours:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Delete all personal data permanently from all our systems (database, files, backups)</li>
          <li>Request deletion from our AI service providers (Mistral AI, Google Gemini)</li>
          <li>Remove all CV analysis results and match scores</li>
          <li>Verify deletion completion across all systems</li>
          <li>Document the incident for compliance purposes</li>
        </ul>

        <p className="text-white/70 leading-relaxed">
          <strong>Our commitment:</strong> Complete and permanent deletion of all data, with no retention for any purpose.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.4 For Parents and Legal Guardians</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>If you believe your child has provided personal data to our Service, please contact us immediately:</strong>
        </p>

        <p className="text-white/70 leading-relaxed mb-2">
          <strong>Email:</strong>{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
        </p>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Subject line:</strong> "Child Privacy Concern - URGENT"
        </p>

        <p className="text-white/80 font-medium mb-2">Include in your message:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>The child's name</li>
          <li>Approximate date they may have used the service</li>
          <li>Any account information you have (email used, username, etc.)</li>
          <li>Your relationship to the child</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-4">
          <strong>We treat these reports with highest priority and will respond within 24 hours.</strong>
        </p>

        <p className="text-white/80 font-medium mb-2">What we'll need from you:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Verification of your parental authority (to protect the child's privacy)</li>
          <li>Any information that helps us identify the account</li>
          <li>Your preferred method of confirmation once deletion is complete</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Your rights as a parent/guardian:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
          <li>Request immediate access to any data collected about your child</li>
          <li>Request immediate deletion of all data</li>
          <li>File a complaint with CNIL (French data protection authority) if you believe we have not acted appropriately</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.5 Our Commitment to Child Safety</h3>
        <p className="text-white/70 leading-relaxed mb-2">We are committed to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Protecting children's privacy online</li>
          <li>Complying with all applicable laws regarding children's data (GDPR Article 8, French law)</li>
          <li>Implementing age verification measures where appropriate</li>
          <li>Training our staff on identifying and handling potential child privacy issues</li>
          <li>Regularly reviewing our practices to ensure children cannot access our Service</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.6 Age Verification Measures</h3>
        <p className="text-white/70 leading-relaxed mb-2">To prevent use by individuals under 18, we:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Require users to confirm they are 18 or older during account creation</li>
          <li>Monitor for suspicious account activity that might indicate underage use</li>
          <li>Reserve the right to request additional age verification (government ID) if we have concerns</li>
          <li>Suspend accounts immediately pending age verification</li>
          <li>Delete accounts if age cannot be verified</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>Important:</strong> Even if a person under 18 provides false information about their age, this does not give us legal authority to process their data. If we discover age misrepresentation, we will delete all data immediately, regardless of any service terms agreed to.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">14.7 Contact for Child Privacy Concerns</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Primary contact:</strong>{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
        </p>

        <p className="text-white/80 font-medium mb-2">French Data Protection Authority (CNIL):</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4 mb-4">
          <li>Website: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#28e675] hover:underline">https://www.cnil.fr</a></li>
          <li>Phone: +33 1 53 73 22 22</li>
          <li>Address: 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France</li>
        </ul>

        <p className="text-white/70 leading-relaxed">
          You may contact CNIL directly at any time if you have concerns about how we handle children's data.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">15. Changes to This Privacy Policy</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We may update this Privacy Policy from time to time to reflect:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Changes in our practices</li>
          <li>Changes in the law</li>
          <li>New features or services</li>
          <li>Feedback from users or regulators</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">How we notify you:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>We will post the updated policy on our website with a new "Last Updated" date</li>
          <li>For significant changes, we will notify you by email</li>
          <li>We may ask for your consent if required by law</li>
        </ul>

        <p className="text-white/70 leading-relaxed">
          <strong>Your responsibility:</strong> Please review this policy periodically to stay informed about how we protect your data.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">16. Contact Us</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>For privacy questions or to exercise your rights:</strong>
        </p>
        <div className="text-white/70 leading-relaxed space-y-2">
          <p>Email: <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a></p>
          <p>Address: 30 Boulevard de Sébastopol 75004</p>
          <p>Response time: We aim to respond to all privacy inquiries within 48 hours.</p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">17. Complaints</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We take your privacy concerns seriously. If you're not satisfied with our response, you have the right to lodge a complaint with the data protection authority:
        </p>
        <div className="text-white/70 leading-relaxed space-y-2">
          <p><strong>CNIL (Commission Nationale de l'Informatique et des Libertés)</strong></p>
          <p>Website: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#28e675] hover:underline">https://www.cnil.fr</a></p>
          <p>Online complaint form: <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer" className="text-[#28e675] hover:underline">https://www.cnil.fr/fr/plaintes</a></p>
          <p>Address: 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France</p>
          <p>Phone: +33 1 53 73 22 22</p>
        </div>
        <p className="text-white/70 leading-relaxed mt-4">
          You can lodge a complaint at any time - you do not need to contact us first.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">18. Additional Information for Specific Users</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">18.1 For Job Candidates</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Your CV is analyzed to help you get fair consideration for jobs</li>
          <li>You control your data - you can view, update, or delete it anytime</li>
          <li>Our AI recommendations do not make hiring decisions</li>
          <li>You can request human review of your AI score</li>
          <li>Your data is retained for up to 2 years to allow consideration for future positions</li>
          <li>You can delete your data anytime</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">18.2 For Recruiters/Clients</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>You are a controller for candidate data you receive through our platform</li>
          <li>You must have your own privacy policy for candidates</li>
          <li>You are responsible for how you use candidate data</li>
          <li>You must comply with GDPR for your processing activities</li>
          <li>We are your processor for CV analysis - see our DPA in Terms of Service</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">Document Information</h2>
        <div className="text-white/70 leading-relaxed space-y-2">
          <p><strong>Version:</strong> 1.0</p>
          <p><strong>Effective Date:</strong> 15/10/2025</p>
          <p><strong>Last Reviewed:</strong> 13/01/2026</p>
          <p><strong>Next Review:</strong> 13/01/2027</p>
          <p><strong>Legal Basis:</strong> GDPR Articles 13-14</p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">Appendix: Glossary</h2>
        <div className="space-y-3">
          <div>
            <p className="text-white/80 font-medium">Personal Data:</p>
            <p className="text-white/70 leading-relaxed">Any information relating to an identified or identifiable person.</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">Processing:</p>
            <p className="text-white/70 leading-relaxed">Any operation performed on personal data (collection, storage, use, disclosure, deletion).</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">Controller:</p>
            <p className="text-white/70 leading-relaxed">The entity that determines the purposes and means of processing personal data.</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">Processor:</p>
            <p className="text-white/70 leading-relaxed">An entity that processes personal data on behalf of the controller.</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">GDPR:</p>
            <p className="text-white/70 leading-relaxed">General Data Protection Regulation - EU privacy law.</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">CNIL:</p>
            <p className="text-white/70 leading-relaxed">Commission Nationale de l'Informatique et des Libertés - French data protection authority.</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">Data Subject:</p>
            <p className="text-white/70 leading-relaxed">The individual whose personal data is being processed (you).</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">Legitimate Interest:</p>
            <p className="text-white/70 leading-relaxed">A legal basis for processing when there's a good reason and individual rights are balanced.</p>
          </div>
          <div>
            <p className="text-white/80 font-medium">Pseudonymization:</p>
            <p className="text-white/70 leading-relaxed">Processing data so it can't be attributed to a person without additional information.</p>
          </div>
        </div>
      </section>
    </>
  ),

  terms: (
    <>
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">1. Introduction and Acceptance</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          Welcome to Talentees. These Terms of Service ("Terms") govern your access to and use of the Talentees platform, website, and services (collectively, the "Service"). The Service is operated by Talentees ("we", "us", or "our").
        </p>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>By accessing or using our Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use the Service.</strong>
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          These Terms apply to all users of the Service, including:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-1 ml-4">
          <li><strong>Job Candidates</strong> who upload CVs or create profiles</li>
          <li><strong>Recruiters and Companies</strong> who subscribe to use our CV analysis services</li>
          <li><strong>Visitors</strong> who browse our website</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">2. Description of Service</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          Talentees provides an AI-powered CV analysis platform that helps recruiters evaluate job candidates by:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Analyzing candidate CVs using artificial intelligence</li>
          <li>Generating match scores based on job requirements</li>
          <li>Providing comparative analysis of multiple candidates</li>
          <li>Highlighting relevant skills and experience</li>
        </ul>
        <p className="text-white/70 leading-relaxed italic">
          <strong>Important:</strong> Our AI provides recommendations only. Final hiring decisions are always made by human recruiters. We do not make automated hiring decisions.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">3. Account Terms</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">3.1 Account Creation</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          To use certain features of the Service, you must create an account. You agree to:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Provide accurate, current, and complete information</li>
          <li>Maintain and update your information to keep it accurate</li>
          <li>Keep your password secure and confidential</li>
          <li>Notify us immediately of any unauthorized access</li>
          <li>Be solely responsible for all activities under your account</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">3.2 Eligibility</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          You must be at least 18 years old to use the Service. By using the Service, you represent that you meet this age requirement.
        </p>
        <p className="text-white/70 leading-relaxed">
          For company accounts, you represent that you have the authority to bind the company to these Terms.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">3.3 Account Termination</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          We reserve the right to suspend or terminate your account if you:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Violate these Terms</li>
          <li>Provide false or misleading information</li>
          <li>Engage in fraudulent or illegal activities</li>
          <li>Fail to pay fees when due</li>
          <li>Use the Service in a way that harms us or other users</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">4. Payment Terms (For Recruiters)</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">4.1 Subscription Plans</h3>
        <p className="text-white/70 leading-relaxed">
          Recruiters must subscribe to a paid plan to access our CV analysis services. Current pricing is available on our website.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">4.2 Billing</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Subscription fees are billed in advance on a monthly or annual basis</li>
          <li>Payments are processed securely by Stripe</li>
          <li>All fees are in Euros (EUR) unless otherwise stated</li>
          <li>Subscriptions automatically renew unless canceled</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">4.3 Refunds</h3>
        <p className="text-white/70 leading-relaxed">
          Subscription fees are non-refundable except as required by law. If you cancel your subscription, you will retain access until the end of your current billing period.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">4.4 Late Payment</h3>
        <p className="text-white/70 leading-relaxed">
          If payment fails, we may suspend your access to the Service until payment is received. We reserve the right to charge late fees or interest on overdue amounts as permitted by law.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">5. Acceptable Use Policy</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.1 Prohibited Activities</h3>
        <p className="text-white/70 leading-relaxed mb-4">You agree NOT to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe on intellectual property rights of others</li>
          <li>Upload malicious code, viruses, or harmful content</li>
          <li>Attempt to gain unauthorized access to our systems</li>
          <li>Scrape, copy, or reverse engineer the Service</li>
          <li>Use the Service for discriminatory hiring practices</li>
          <li>Share your account credentials with others</li>
          <li>Interfere with or disrupt the Service</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.2 Recruiter Obligations</h3>
        <p className="text-white/70 leading-relaxed mb-4">If you are a recruiter, you must:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Have a legal basis to process candidate personal data</li>
          <li>Maintain your own privacy policy for candidates</li>
          <li>Comply with all applicable data protection laws (including GDPR)</li>
          <li>Ensure candidates know their data will be processed by AI</li>
          <li>Not use our Service for discriminatory purposes</li>
          <li>Make final hiring decisions yourself (not rely solely on AI scores)</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">5.3 Candidate Obligations</h3>
        <p className="text-white/70 leading-relaxed mb-4">If you are a candidate, you must:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Provide accurate information in your CV</li>
          <li>Have the right to upload and share the information in your CV</li>
          <li>Not upload others' personal data without consent</li>
          <li>Not upload copyrighted material you don't own</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">6. Data Processing Agreement (For Recruiters)</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>Important:</strong> When recruiters use our platform to process candidate CVs, GDPR Article 28 requires a written agreement between the data controller (recruiter) and data processor (us). This section serves as that agreement.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.1 Roles and Responsibilities</h3>

        <p className="text-white/80 font-medium mb-2">Data Controller (Recruiter):</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>You determine the purposes and means of processing candidate data</li>
          <li>You are responsible for having a legal basis to process candidate data</li>
          <li>You must have your own privacy policy for candidates</li>
          <li>You are responsible for responding to candidate rights requests</li>
        </ul>

        <p className="text-white/80 font-medium mb-2">Data Processor (Talentees):</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>We process candidate data only on your instructions</li>
          <li>We implement appropriate security measures</li>
          <li>We assist you in responding to candidate rights requests</li>
          <li>We delete or return data when our service ends</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.2 Scope of Processing</h3>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li><strong>Subject matter:</strong> CV analysis and candidate matching</li>
          <li><strong>Duration:</strong> For the duration of your subscription and up to 2 years for archived candidates</li>
          <li><strong>Nature and purpose:</strong> Analyzing candidate qualifications, generating match scores, comparing candidates</li>
          <li><strong>Personal data types:</strong> Names, contact details, work history, education, skills, professional achievements</li>
          <li><strong>Data subjects:</strong> Job candidates</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.3 Your Instructions to Us</h3>
        <p className="text-white/70 leading-relaxed mb-2">You instruct us to process candidate data to:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Analyze CVs using our AI technology</li>
          <li>Generate match scores based on job requirements</li>
          <li>Store data for the retention periods specified in our Privacy Policy</li>
          <li>Provide you with access to candidate profiles and analysis</li>
          <li>Delete data upon your request or at end of retention period</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.4 Our Security Obligations</h3>
        <p className="text-white/70 leading-relaxed mb-2">We implement appropriate technical and organizational measures including:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
          <li>Role-based access controls</li>
          <li>Regular security audits and penetration testing</li>
          <li>Staff confidentiality agreements</li>
          <li>24/7 security monitoring</li>
          <li>Secure backup systems</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.5 Sub-Processors</h3>
        <p className="text-white/70 leading-relaxed mb-2">You authorize us to use the following sub-processors:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li><strong>Mistral AI (France)</strong> - AI-powered CV analysis</li>
          <li><strong>OpenAI (United States)</strong> - AI-powered CV analysis via GPT API</li>
          <li><strong>Microsoft Azure (United States)</strong> - Application hosting and data storage (US East region)</li>
          <li><strong>Stripe (United States)</strong> - Payment processing</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          We will notify you at least 30 days before adding or replacing sub-processors. You may object to any new sub-processor on reasonable data protection grounds. If we cannot accommodate your objection, you may terminate your subscription.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.6 International Data Transfers</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          Some of our sub-processors are located in the United States. We protect international transfers through:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
          <li>Data Processing Agreements with all sub-processors</li>
          <li>Additional technical security measures (encryption, access controls)</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          Copies of our SCCs are available upon request at{' '}
          <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a>
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.7 Data Breach Notification</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          We will notify you within 24 hours of becoming aware of any personal data breach that affects candidate data you control. Our notification will include:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Nature of the breach</li>
          <li>Categories and approximate number of affected data subjects</li>
          <li>Likely consequences</li>
          <li>Measures taken or proposed to address the breach</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.8 Assistance with Data Subject Rights</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          We will assist you in responding to candidate rights requests (access, rectification, erasure, restriction, portability, objection) by:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Providing technical means for you to fulfill requests</li>
          <li>Responding within 7 days of your request for assistance</li>
          <li>Implementing requested deletions, corrections, or restrictions</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.9 Data Deletion and Return</h3>
        <p className="text-white/70 leading-relaxed mb-2">Upon termination of your subscription, we will:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-2">
          <li>Delete all candidate data within 30 days, OR</li>
          <li>Return the data to you in a structured format if you request</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          <strong>Exception:</strong> We may retain data as required by law (e.g., accounting records for 10 years)
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">6.10 Audits and Inspections</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          You may request information about our data processing practices once per year. We will provide:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Security certifications (ISO 27001, SOC 2)</li>
          <li>Copies of our DPAs with sub-processors</li>
          <li>Security audit reports (redacted for confidentiality)</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          On-site audits may be conducted with 30 days notice and at your expense.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">7. Intellectual Property</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">7.1 Our Intellectual Property</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          The Service, including all software, code, algorithms, designs, graphics, and content (excluding user-uploaded content), is owned by Talentees and protected by copyright, trademark, and other intellectual property laws.
        </p>
        <p className="text-white/70 leading-relaxed mb-2">
          We grant you a limited, non-exclusive, non-transferable license to access and use the Service for its intended purpose. You may not:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Copy, modify, or create derivative works</li>
          <li>Reverse engineer or decompile the Service</li>
          <li>Remove any copyright or proprietary notices</li>
          <li>Use our trademarks without permission</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">7.2 User Content</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          You retain ownership of content you upload (CVs, job descriptions, etc.). By uploading content, you grant us a worldwide, non-exclusive license to:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Store and process your content to provide the Service</li>
          <li>Use anonymized, aggregated data to improve our AI models</li>
        </ul>
        <p className="text-white/70 leading-relaxed italic">
          <strong>Important:</strong> We do NOT train our AI models on individual CVs or identifiable data without explicit consent. We only use anonymized, aggregated data for improvements.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">7.3 AI-Generated Outputs</h3>
        <p className="text-white/70 leading-relaxed">
          Match scores, analysis reports, and other AI-generated outputs based on your data are provided to you for your use in hiring decisions. You may use these outputs for your internal recruitment purposes.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">8. Disclaimers and Limitations of Liability</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.1 Service Availability</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          The Service is provided "AS IS" and "AS AVAILABLE." We do not guarantee that the Service will be uninterrupted, error-free, or completely secure. We reserve the right to:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Modify or discontinue features</li>
          <li>Perform scheduled maintenance</li>
          <li>Update our software and systems</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.2 AI Accuracy Disclaimer</h3>
        <p className="text-white/70 leading-relaxed mb-4">
          <strong>IMPORTANT:</strong> Our AI provides recommendations and analysis to assist hiring decisions. We do NOT guarantee the accuracy, completeness, or appropriateness of AI-generated scores or recommendations.
        </p>
        <p className="text-white/70 leading-relaxed mb-2">Recruiters must:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Review candidates themselves (not rely solely on AI scores)</li>
          <li>Make final hiring decisions using human judgment</li>
          <li>Ensure their hiring practices comply with all applicable laws</li>
          <li>Not use our Service for discriminatory purposes</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.3 No Warranties</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Warranties of merchantability</li>
          <li>Fitness for a particular purpose</li>
          <li>Non-infringement</li>
          <li>Accuracy or reliability of results</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.4 Limitation of Liability</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TALENTEES SHALL NOT BE LIABLE FOR:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Indirect, incidental, special, consequential, or punitive damages</li>
          <li>Loss of profits, revenue, data, or business opportunities</li>
          <li>Hiring decisions made based on our AI recommendations</li>
          <li>Actions of third parties (sub-processors, payment providers)</li>
        </ul>
        <p className="text-white/70 leading-relaxed">
          Our total liability for all claims shall not exceed the amount you paid us in the 12 months prior to the claim, or €500, whichever is greater.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">8.5 Indemnification</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          You agree to indemnify and hold Talentees harmless from any claims, damages, or costs (including legal fees) arising from:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Your violation of these Terms</li>
          <li>Your violation of any law or regulation</li>
          <li>Your hiring decisions or employment practices</li>
          <li>Content you upload to the Service</li>
          <li>Your infringement of third-party rights</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">9. Termination</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.1 Termination by You</h3>
        <p className="text-white/70 leading-relaxed">
          You may cancel your subscription at any time through your account settings or by contacting{' '}
          <a href="mailto:support@talentees.net" className="text-[#28e675] hover:underline">support@talentees.net</a>.
          Cancellation takes effect at the end of your current billing period.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.2 Termination by Us</h3>
        <p className="text-white/70 leading-relaxed mb-2">
          We may suspend or terminate your account immediately if you:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Violate these Terms</li>
          <li>Fail to pay fees</li>
          <li>Engage in fraudulent or illegal activity</li>
          <li>Harm or threaten the Service or other users</li>
        </ul>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">9.3 Effects of Termination</h3>
        <p className="text-white/70 leading-relaxed mb-2">Upon termination:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4">
          <li>Your access to the Service ends immediately</li>
          <li>We will delete your data per our Privacy Policy and Section 6.9</li>
          <li>No refunds for partial billing periods</li>
          <li>Sections 6-8 and 10-12 of these Terms survive termination</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">10. Dispute Resolution and Governing Law</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.1 Governing Law</h3>
        <p className="text-white/70 leading-relaxed">
          These Terms are governed by the laws of France, without regard to conflict of law principles.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.2 Jurisdiction</h3>
        <p className="text-white/70 leading-relaxed">
          Any disputes arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Paris, France.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">10.3 Informal Resolution</h3>
        <p className="text-white/70 leading-relaxed">
          Before filing any legal claim, you agree to attempt to resolve the dispute informally by contacting us at{' '}
          <a href="mailto:support@talentees.net" className="text-[#28e675] hover:underline">support@talentees.net</a>.
          We will attempt to resolve the dispute within 30 days.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">11. Changes to These Terms</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We may update these Terms from time to time to reflect:
        </p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Changes in our services or business practices</li>
          <li>Changes in applicable laws</li>
          <li>New features or functionality</li>
        </ul>

        <p className="text-white/70 leading-relaxed mb-2">When we make material changes, we will:</p>
        <ul className="list-disc list-inside text-white/70 leading-relaxed space-y-2 ml-4 mb-4">
          <li>Update the "Last Updated" date at the top</li>
          <li>Notify you by email or through the Service</li>
          <li>Give you at least 30 days notice before changes take effect</li>
        </ul>

        <p className="text-white/70 leading-relaxed">
          Your continued use of the Service after changes take effect constitutes acceptance of the new Terms. If you disagree with the changes, you must stop using the Service and cancel your subscription.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">12. Miscellaneous</h2>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.1 Entire Agreement</h3>
        <p className="text-white/70 leading-relaxed">
          These Terms, together with our Privacy Policy, constitute the entire agreement between you and Talentees regarding the Service.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.2 Severability</h3>
        <p className="text-white/70 leading-relaxed">
          If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.3 Waiver</h3>
        <p className="text-white/70 leading-relaxed">
          Our failure to enforce any provision does not constitute a waiver of that provision.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.4 Assignment</h3>
        <p className="text-white/70 leading-relaxed">
          You may not transfer or assign your rights under these Terms without our written consent. We may assign our rights and obligations to any party.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.5 Force Majeure</h3>
        <p className="text-white/70 leading-relaxed">
          We are not liable for delays or failures due to causes beyond our reasonable control, including natural disasters, war, terrorism, riots, pandemics, government actions, or infrastructure failures.
        </p>

        <h3 className="text-xl font-semibold text-white/85 mb-3 mt-6">12.6 Language</h3>
        <p className="text-white/70 leading-relaxed">
          These Terms are provided in English. In case of conflict between English and any translation, the English version prevails.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">13. Contact Information</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          If you have questions about these Terms, please contact us:
        </p>
        <div className="text-white/70 leading-relaxed space-y-2">
          <p><strong>Email:</strong> <a href="mailto:support@talentees.net" className="text-[#28e675] hover:underline">support@talentees.net</a></p>
          <p><strong>Legal inquiries:</strong> <a href="mailto:legal@talentees.net" className="text-[#28e675] hover:underline">legal@talentees.net</a></p>
          <p><strong>Privacy matters:</strong> <a href="mailto:privacy@talentees.net" className="text-[#28e675] hover:underline">privacy@talentees.net</a></p>
          <p><strong>Address:</strong> 30 Boulevard de Sébastopol 75004</p>
          <p><strong>Company Registration:</strong> 992 604 942 00019</p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white/90 mb-4">Document Information</h2>
        <div className="text-white/70 leading-relaxed space-y-2">
          <p><strong>Version:</strong> 1.0</p>
          <p><strong>Effective Date:</strong> 15/10/2025</p>
          <p><strong>Last Reviewed:</strong> 13/01/2026</p>
          <p><strong>Next Review:</strong> 13/01/2027</p>
        </div>
      </section>
    </>
  ),

  dpa: (
    <>
      <section className="mb-12">
        <p className="text-white/70 leading-relaxed italic">
          Ce document sera complété prochainement avec notre Accord de Traitement des Données (DPA) conforme au RGPD.
        </p>
      </section>
    </>
  ),

  'candidates-privacy': (
    <>
      <section className="mb-12">
        <p className="text-white/70 leading-relaxed italic">
          Ce document sera complété prochainement avec notre Avis de confidentialité destiné aux candidats.
        </p>
      </section>
    </>
  ),

  cookies: (
    <>
      <section className="mb-12">
        <p className="text-white/70 leading-relaxed italic">
          Ce document sera complété prochainement avec notre Politique d'utilisation des cookies.
        </p>
      </section>
    </>
  ),
};
